<template>
  <div class="request" redesign>
    <!-- Header -->
    <div class="local-hero local-hero-dark no-padding">
      <div class="local-hero-graphic">
        <div class="local-hero-header-wrapper">
          <h1 class="local-hero-header" v-translate>Make a grant request</h1>
        </div>
        <div class="local-hero-graphic-wrapper hidden-mobile">
          <div class="local-hero-graphic-holder">
            <PorchSvg :color="organizationDetails.darkBrandColor" />
          </div>
        </div>
      </div>
    </div>

    <div class="request-form form-container">
      <!-- Hardship definition -->
      <div class="form-field">
        <div class="hardship-definition eligibility-list" v-html="hardshipDefinition"></div>
      </div>

      <!-- Eligibility list -->
      <div class="form-field">
        <div class="eligibility-list">
          <p v-translate>To make a grant request, you will need to do the following:</p>
          <ul>
            <li>
              <span v-translate>Provide payment information so you can receive grant money</span>
            </li>
            <li v-if="organizationDetails.eventDocumentationEnabled">
              <span v-translate>RequestPage Describe and document your emergency</span>
            </li>
            <li v-if="!organizationDetails.eventDocumentationEnabled">
              <span v-translate>RequestPage Describe your emergency</span>
            </li>
            <li
              v-if="
                !organizationDetails.expenseDocumentationEnabled &&
                  !organizationDetails.grantRequestAmountEnabled
              "
            >
              <span v-translate>RequestPage Describe expenses</span>
            </li>
            <li v-if="organizationDetails.grantRequestAmountEnabled">
              <span v-translate>RequestPage Describe expenses and amounts needed</span>
            </li>
            <li v-if="organizationDetails.expenseDocumentationEnabled">
              <span v-translate>Document expenses</span>
            </li>
            <li>
              <span v-translate>Review and submit your request</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Information card -->
      <div class="form-field info-container">
        <div class="tooltip-icon">
          <img src="@/assets/icons/information-icon.png" />
        </div>
        <div class="tooltip">
          <span class="tooltip-text" v-translate>
            You can pause at any point if you need more time to enter details. The information you
            have entered will be saved automatically. You can return to your request when you are
            ready.
          </span>
        </div>
      </div>

      <!-- GC T&C Agreement -->
      <div class="form-field your-commitment">
        <h4 class="your-commitment-heading" v-translate>Your commitment to the %{organizationDetails.programName}</h4>

        <!-- Error -->
        <div v-if="hasSubmitted && !userCertified" class="form-field-container error-container">
          <div class="tooltip-icon">
            <img src="@/assets/icons/error-icon.png" />
          </div>
          <div class="tooltip">
            <h4 class="tooltip-header" v-translate>Response required</h4>
            <span class="tooltip-text" v-translate>
              To proceed with your grant request, please check the box to indicate your agreement
              with the statement below.
            </span>
          </div>
        </div>

        <!-- T&C -->
        <div class="form-field">
          <div class="checkboxes">
            <label class="checkbox">
              <input type="checkbox" id="termsAndConditions" v-model="userCertified" />
              <span
                class="form-checkbox"
                :class="{ 'form-checkbox-error': hasSubmitted && !userCertified }"
                data-action="accept-terms" 
              />
              <span v-translate>
                By checking the box below, I certify that all of the information that will be
                submitted in this request is true and correct to the best of my knowledge. I also
                certify that I am requesting funds to overcome a financial emergency.
              </span>
            </label>
          </div>
        </div>

        <!-- Submit -->
        <SubmitButton :onContinue="startRequest" actionName="start-request" />
      </div>
    </div>

    <!-- Modals -->
    <!-- Grant limit modal -->
    <Modal
      v-if="grantLimitModal"
      dimmed
      :additionalContainerStyles="{
        width: '100%',
        overflow: 'hidden',
        'justify-content': 'center',
      }"
      :additionalModalStyles="{
        width: '90%',
        'max-width': '500px',
        border: 'none',
        'overflow-y': 'scroll',
        'max-height': '90%',
      }"
    >
      <div class="form-container card-shape modal-window">
        <div class="fcolumn">
          <div class="frow maxedModalSection">
            <div
              class="frow"
              v-if="organizationDetails.grantLimitDuration == 'Lifetime'"
              v-translate="{ grantlimit: grantLimitDollars }"
            >
              Unfortunately, you have already reached the total grant limit of %{grantlimit}, or
              received four grants.
            </div>
            <div
              class="frow"
              v-if="organizationDetails.grantLimitDuration == 'Calendar Year'"
              v-translate="{ grantlimit: grantLimitDollars }"
            >
              Unfortunately, you have already reached the total grant limit of %{grantlimit}, or
              received four grants, in this calendar year.
            </div>
            <div
              class="frow"
              v-if="organizationDetails.grantLimitDuration.startsWith('Rolling')"
              v-translate="{
                grantlimit: grantLimitDollars,
                num: organizationDetails.rollingGrantDuration,
              }"
            >
              Unfortunately, you have already reached the total grant limit of %{grantlimit}, or
              received four grants, in a rolling %{num}-year period.
            </div>
          </div>
          <div class="frow maxedModalSection">
            <div
              class="frow"
              v-if="organizationDetails.grantLimitDuration == 'Calendar Year'"
              v-translate
            >
              You will be able to request additional grant funds on January 1.
            </div>
            <div
              class="frow"
              v-if="organizationDetails.grantLimitDuration.startsWith('Rolling')"
              v-translate="{
                date: dateToReApply,
              }"
            >
              You will be able to request additional grant funds on %{date}.
            </div>
          </div>
          <div class="frow maxedModalSection" v-if="organizationDetails.additionalResourcesCopy">
            <div v-html="additionalResources"></div>
          </div>
          <div class="frow maxedModalSection">
            <div v-translate>
              We are sorry you are experiencing financial hardship. We wish you the best.
            </div>
          </div>
          <div class="frow maxedModalSection" style="justify-content: center">
            <router-link
              :to="{ name: 'Home' }"
              tag="button"
              class="form-button"
              style="color: white;"
            >
              <span v-translate>Home</span>
            </router-link>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Eligibility message modal -->
    <Modal
      v-if="!user.isEligible && eligibilityModal"
      dimmed
      :width="95"
      @close-modal="eligibilityModal = false; $router.push({ name: 'Home' })"
    >
      <div class="form-container card-shape modal-window">
        <h2 class="form-field-header" v-translate>You are not yet eligible to make a grant request.</h2>
        <div class="form-field-container">
          <p v-translate="{
              orgName: organizationDetails.name,
              periodLength: organizationDetails.eligibilityPeriodNumDays,
              daysLeft: user.daysUntilEligible,
              infoEmail: organizationDetails.supportEmail,
            }"
          >
            %{orgName} requires that you remain with the company for %{periodLength} days before you are eligible to request a grant. You have %{daysLeft} remaining until you become eligible. Reach out to %{infoEmail} with any questions, issues, or feedback.
          </p>
        </div>
        <div class="button-container">
          <button class="form-button" @click="$router.push({ name: 'Home' })"><span v-translate>I Understand</span></button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { formatDateShort, formatNumberAsUSD } from '@/services/utils'
import Request from '@/services/requests'
import EventCategory from '@/services/eventCategories'
import { mapState } from 'vuex'
import LoadSpinner from '@/components/LoadSpinner'
import { analyticsMixin } from '@/services/googleAnalytics'
import Action from '@/services/actions'
import CollectionManager from '@/services/collectionManager'
import ActivityCard from '@/components/myActivity/ActivityCard'
import Modal from '@/components/Modal'
import { translateToCurrentLang, formatValue, formatText } from '@/services/utils'
import UserEvent from '@/services/userEvents/models'
import SurveyResponse from '@/services/surveyresponses'
import PorchSvg from '@/views/svg/PorchSvg'
import SubmitButton from '@/components/requests/SubmitButton'

const REQUEST_STARTED = 'REQUEST_STARTED'
const STARTED = 'STARTED'

export default {
  name: 'Request',
  mixins: [analyticsMixin],
  components: {
    LoadSpinner,
    ActivityCard,
    Modal,
    PorchSvg,
    SubmitButton,
  },
  data() {
    return {
      userCertified: false,
      loading: false,
      startedRequests: CollectionManager.create({
        ModelClass: Action,
        filters: {
          ordering: '-datetime_created',
          actionType: REQUEST_STARTED,
          requestStatus: STARTED,
        },
      }),
      showResumeRequestModal: false,
      grantLimitModal: false,
      hasSubmitted: false,
      eligibilityModal: false,
      reproductiveRightsRequest: false
    }
  },
  async beforeCreate() {
    if (Object.keys(this.$route.params).includes('reproductive_rights')) {
        this.reproductiveRightsRequest = true
    }
  },
  async created() {
    if (!this.maxedGrantLimit && this.user.isEligible) {
      this.startedRequests.refresh().then(() => {
        if (this.startedRequests.list.length) {
          // NOTE (Bruno 7-13-20): As per client's request, the modal is tracked as its own page/pageview
          this.trackCustomPageview('ResumeStartedRequest', '/resume-started-request')

          this.showResumeRequestModal = true
        }
      })
    } else if (await this.blockRequest()) {
    }
  },
  methods: {
    closeModal() {
      // NOTE (Bruno 7-14-20): As per client's request, the modal is tracked as its own page/pageview
      this.analyticsEventsTracker(
        'clicked-cta',
        'start a new request',
        'ResumeStartedRequest',
        '/resume-started-request',
      )
      this.showResumeRequestModal = false
    },
    async blockRequest() {
      if (this.maxedGrantLimit) {
        this.grantLimitModal = true
        await UserEvent.api.requestStartBlocked()
        this.trackCustomPageview('RequestStartBlockedMaxedGrantLimit', '/request-start-blocked')
        return true
      } else if(!this.user.isEligible) {
        this.eligibilityModal = true
        return true
      }
      return false
    },
    async startRequest() {
      if (await this.blockRequest()) return false

      this.loading = true
      this.hasSubmitted = true

      if (this.hasSubmitted && !this.userCertified) {
        this.loading = false
        return false
      }

      this.trackClickedCTA()

      if (!this.reproductiveRightsRequest) {
        await Request.api
          .create({})
          // Update user's current request in state
          .then(request => {
            this.$store.dispatch('updateCurrentRequest', request)
          })
      }
    },
  },
  computed: {
    ...mapState(['user', 'currentRequest', 'organizationDetails', 'defaultRequestFlow']),
    grantLimitDollars() {
      return formatNumberAsUSD(this.organizationDetails.grantLimit, true)
    },
    dateToReApply() {
      // Push this logic to backend if we need to use this in multiple places
      var initial_date = new Date(this.user.rollingGrantDate).getTime()
      var final_date = new Date(
        initial_date + this.organizationDetails.rollingGrantDuration * 365 * 24 * 60 * 60 * 1000,
      )
      return formatDateShort(final_date.getTime(), this.$language.current)
    },
    disabled() {
      if (!this.userCertified || this.loading) {
        return true
      }
      return false
    },
    additionalResources() {
      return this.organizationDetails.additionalResourcesCopy
    },
    hardshipDefinition() {
      const varMap = { org: this.organizationDetails }
      return formatValue(this.organizationDetails.hardshipDefinitionCopy, varMap)
    },
    maxedGrantLimit() {
      if (!this.organizationDetails.grantLimit) {
        return false
      }
      if (
        this.organizationDetails.grantLimitDuration == 'Lifetime' &&
        this.user.totalPayouts == this.organizationDetails.grantLimit
      ) {
        return true
      }
      if (this.user.totalPayoutsSinceLastGrantLimitReset == this.organizationDetails.grantLimit) {
        return true
      }
      return false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/flex-box';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/grant-circles';

.maxedModalSection {
  margin: 1rem 0 0 0;
  justify-content: start;
  align-items: center;
}

.request {
  display: flex;
  flex-direction: column;

  .request-form {
    max-width: 704px;
    align-self: center;

    @include desktop-screen {
      max-width: 1076px;
      align-self: center;
    }

    text-align: left;

    .eligibility-list {
      ::v-deep p:first-of-type {
        font-size: 1.5rem;
        font-weight: bold;
        line-height: 2rem;
      }

      ::v-deep ul {
        display: flex;
        flex-direction: column;
        list-style: none;
        padding-left: 0.5rem;
      }

      ::v-deep li {
        display: flex;
        flex-direction: row;
        font-size: 1rem;
        line-height: 1.5rem;
        margin-bottom: 1rem;
        margin-left: 0;

        &::before {
          background: $black;
          border-radius: 50%;
          content: '';
          position: relative;
          top: 6px;
          height: 12px;
          width: 12px;
          min-width: 12px;
          max-width: 12px;
          margin-right: 1rem;
        }
      }
    }

    .your-commitment {
      @extend .card-shape;

      @include tablet-and-up-screen {
        padding: 2.5rem;
      }

      .your-commitment-heading {
        font-size: 1.5rem;
        line-height: 2rem;
        font-weight: bold;
      }

      .checkbox span.form-checkbox + span {
        font-family: 'Helvetica Neue';
        font-size: 18px;
        line-height: 28px;
      }
    }

    .button-container {
      margin-bottom: 0;
    }
  }
}

.activity-container {
  width: 90%;
  border-top: 1px solid #dfdfe0;
  border-bottom: 1px solid #dfdfe0;
}
</style>
